import EnemyEntity from '../../entities/EnemyEntity';
import { EnemyBehaviorParams } from '../../data';

export interface IEnemyAI {
    update(enemy: EnemyEntity, player: { x: number, y: number }, dt: number): void;
}

export class ChaseAI implements IEnemyAI {
    update(enemy: EnemyEntity, player: { x: number, y: number }, _dt: number): void {
        const dx = player.x - (enemy as any).x; // Accessing x/y from sprite wrapper or need getter
        const dy = player.y - (enemy as any).y;

        // Simple normalization
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > 5) { // Stop jittering when close
            const vx = (dx / distance) * enemy.speed;
            const vy = (dy / distance) * enemy.speed;

            // Assume enemy has a method to set velocity or we access body directly
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(vx, vy);
            }
        } else {
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(0, 0);
            }
        }
    }
}

export class DashAI implements IEnemyAI {
    private dashCooldown: number = 0;
    private isDashing: boolean = false;
    private dashTime: number = 0;
    private dashVector: { x: number, y: number } = { x: 0, y: 0 };
    private readonly dashCooldownSeconds: number;
    private readonly dashDurationSeconds: number;
    private readonly dashSpeedMultiplier: number;
    private readonly dashMinDistance: number;
    private readonly dashMaxDistance: number;

    constructor(params?: EnemyBehaviorParams) {
        this.dashCooldownSeconds = toNonNegative(params?.dashCooldown, 2);
        this.dashDurationSeconds = toPositive(params?.dashDuration, 0.5);
        this.dashSpeedMultiplier = toPositive(params?.dashSpeedMultiplier, 3);
        this.dashMinDistance = toNonNegative(params?.dashMinDistance, 50);
        this.dashMaxDistance = toNonNegative(params?.dashMaxDistance, 200);
    }

    update(enemy: EnemyEntity, player: { x: number, y: number }, dt: number): void {
        if (this.isDashing) {
            this.dashTime -= dt;
            // Continue dashing in vector
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(
                    this.dashVector.x * enemy.speed * this.dashSpeedMultiplier,
                    this.dashVector.y * enemy.speed * this.dashSpeedMultiplier
                );
            }

            if (this.dashTime <= 0) {
                this.isDashing = false;
                this.dashCooldown = this.dashCooldownSeconds;
            }
            return;
        }

        this.dashCooldown -= dt;

        // Normal chase behavior when not dashing
        const dx = player.x - (enemy as any).x;
        const dy = player.y - (enemy as any).y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (this.dashCooldown <= 0 && distance < this.dashMaxDistance && distance > this.dashMinDistance) {
            // Start Dash
            this.isDashing = true;
            this.dashTime = this.dashDurationSeconds;
            if (distance > 0) {
                this.dashVector = { x: dx / distance, y: dy / distance };
            } else {
                this.dashVector = { x: 0, y: 0 };
            }
        } else {
            // Standard chase
            if (distance > 5) {
                const vx = (dx / distance) * enemy.speed;
                const vy = (dy / distance) * enemy.speed;
                if ((enemy as any).physicsBody) {
                    (enemy as any).physicsBody.setVelocity(vx, vy);
                }
            } else {
                if ((enemy as any).physicsBody) {
                    (enemy as any).physicsBody.setVelocity(0, 0);
                }
            }
        }
    }
}

export class RangedAI implements IEnemyAI {
    private readonly preferredDistance: number;
    private readonly retreatDistance: number;

    constructor(params?: EnemyBehaviorParams) {
        this.preferredDistance = toNonNegative(params?.rangedPreferredDistance, 200);
        this.retreatDistance = toNonNegative(params?.rangedRetreatDistance, 150);
    }

    update(enemy: EnemyEntity, player: { x: number, y: number }, _dt: number): void {
        const dx = player.x - (enemy as any).x;
        const dy = player.y - (enemy as any).y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance <= 0) {
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(0, 0);
            }
            return;
        }

        // Keep distance around configured preferred/retreat range
        if (distance > this.preferredDistance) {
            // Approach
            const vx = (dx / distance) * enemy.speed;
            const vy = (dy / distance) * enemy.speed;
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(vx, vy);
            }
        } else if (distance < this.retreatDistance) {
            // Retreat
            const vx = -(dx / distance) * enemy.speed;
            const vy = -(dy / distance) * enemy.speed;
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(vx, vy);
            }
        } else {
            // Stop and (todo) shoot
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(0, 0);
            }
        }
    }
}

export class BossAI implements IEnemyAI {
    update(enemy: EnemyEntity, player: { x: number, y: number }, _dt: number): void {
        const dx = player.x - (enemy as any).x;
        const dy = player.y - (enemy as any).y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Boss always chases but maybe slower or with unique logic later
        if (distance > 5) {
            const vx = (dx / distance) * enemy.speed;
            const vy = (dy / distance) * enemy.speed;
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(vx, vy);
            }
        } else {
            if ((enemy as any).physicsBody) {
                (enemy as any).physicsBody.setVelocity(0, 0);
            }
        }
    }
}

export type EnemyAIConstructor = new (params?: EnemyBehaviorParams) => IEnemyAI;

export const AI_REGISTRY: Record<string, EnemyAIConstructor> = {
    'chase': ChaseAI,
    'dash': DashAI,
    'ranged': RangedAI,
    'boss': BossAI
};

function toNonNegative(value: number | undefined, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function toPositive(value: number | undefined, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}
